import { useMemo } from "react";
import HomeCleaningIcon from "@/assets/images/3d-icons/home-cleaning.png";
import type { TodoCardProps } from "../TodoCard";
import { useGetVisitsQuery } from "@/gqlhooks/visit/useGetVisitsQuery";
import {
  JobScheduleTypeEnum,
  PackageRecurrenceEnum,
  SortEnum,
  VisitSortByEnum,
} from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";
import { useRoute } from "@/components/shared/router";
import {
  getRebookAdHocState,
  useBookingState,
} from "@/store/booking/useBookingState";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import {
  getBookingPackages,
  getDepartment,
  getServiceNameFromDepartment,
} from "@/lib/booking-lib";
import { formatDate, getTimeDifferenceHours } from "@/lib/helpers/date";
import { addDays, startOfToday } from "date-fns";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { otherServiceItems, popularServiceItems } from "../../lib";

const services = [...popularServiceItems(), ...otherServiceItems()];

// REFACTOR: this logic should be in the backend
// Return the recent booking in the past 30 days
// only if the user does not have any upcoming one-time booking in the next 7 days.
export const usePreviousBookingCard = (): TodoCardProps | null => {
  const description = "Rebook";
  const { push } = useRoute();
  const { setBookingState } = useBookingState();
  const { fetch: getAllClientPackage } = useAllClientPackageDetailsStore();
  const clientId = useAuthState((state) => state.data.userInfo.id);
  const past30DaysQuery = useGetVisitsQuery({
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId: [clientId],
        from: formatDate(addDays(startOfToday(), -30)),
        to: formatDate(addDays(startOfToday(), -1)),
        sortBy: [VisitSortByEnum.ServiceDate],
        sort: [SortEnum.Desc],
      },
    },
  });
  const next7DaysQuery = useGetVisitsQuery({
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId: [clientId],
        from: formatDate(startOfToday()),
        to: formatDate(addDays(startOfToday(), 6)),
        sortBy: [VisitSortByEnum.ServiceDate],
        sort: [SortEnum.Asc],
      },
    },
  });

  const previousBooking = useMemo(() => {
    const hasUpcomingOneTimeBooking = next7DaysQuery.list.some(
      (visit) => visit.scheduleType === JobScheduleTypeEnum.OneTime,
    );
    if (hasUpcomingOneTimeBooking) return null;
    return past30DaysQuery.list[0];
  }, [past30DaysQuery.list, next7DaysQuery.list]);

  if (!previousBooking) return null;

  const serviceName = getServiceNameFromDepartment(previousBooking.department);
  const service = services.find((service) => service.name === serviceName);

  const handleRebook = () => {
    const department = getDepartment(serviceName);
    const timeDiff = getTimeDifferenceHours(
      previousBooking?.startTime || "",
      previousBooking?.endTime || "",
    );
    const filters = {
      repeatEvery: [PackageRecurrenceEnum.AdHoc],
      repeatEveryTimes: 0,
      duration: timeDiff,
    };

    const baseState = getRebookAdHocState(timeDiff);
    setBookingState({
      ...baseState,
      department,
      currentPath: `/booking/${serviceName}/select-slot`,
    });

    getAllClientPackage({
      requestPayload: {
        filters: {
          ...filters,
          department,
          active: true,
        },
      },
    }).then((res) => {
      if (res.error || !res.data) {
        return;
      }
      const packages = getBookingPackages(department, res.data);
      sendAnalyticData({ name: "rebook_click" });
      setBookingState({ selectedPackageIds: packages.map((pkg) => pkg.id) });
      push(`/booking/${serviceName}/select-slot`);
    });
  };

  return {
    onClick: handleRebook,
    image: service?.icon || HomeCleaningIcon,
    title: service?.title || "Service",
    desc: description,
  };
};
